import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "@/components/ui/Card.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import {
  FOLA_COMPACT_HEADER_ROW_CLASS,
  FOLA_ICON_CLASS,
  FOLA_LABEL_CLASS,
} from "@/components/fola/folaCardStyles.js";
import { loadPatientIntroPreview } from "@/lib/patientIntroPreview";
import {
  DOCTOR_PAYMENT_METHOD_OPTIONS,
  parseDoctorPayoutMeta,
} from "@/services/doctorPaymentPreferences.js";

const FOLA_ICON = "/icons/appointments/Chat with Fola/Fola.svg";
const CLOSE_ICON = "/icons/pharmacy/circle-x.svg";
const DOCTOR_DEV_PHOTO_KEY = "afrimed:dev:doctor_profile_photo";
const PATIENT_PROFILE_STEP_KEY = "patient-profile-completion-v4";
const DOCTOR_CONSULTANCY_STEP_KEY = "doctor-consultancy-setup-v2";
const DOCTOR_READINESS_STEP_KEY = "doctor-readiness-orchestration";
const DOCTOR_READINESS_SESSION_COMPLETE_KEY = "afrimed:fola:doctor-readiness-session-complete";
const DOCTOR_READINESS_STEP_COMPLETE_EVENT = "afrimed:fola:step-complete";
const DOCTOR_READINESS_NEXT_STEP_EVENT = "afrimed:fola:next-step";
const PATIENT_GLOBAL_DISMISSAL_KEY = "patient-fola-smartcards-global-dismissed";
const FINAL_VISIBILITY_RULES_ENABLED = true;
const VALID_FOLA_ROLES = new Set(["patient", "doctor"]);
// Only these persisted fields are readiness requirements. Everything else in
// this component is first-use orientation or optional presentation and must
// never create a Smart Card by itself.
const PATIENT_PERSISTED_READINESS_ITEMS = new Set([
  "date_of_birth",
  "id_passport",
  "languages",
  "address",
  "blood_type",
  "allergies",
  "medical_conditions",
  "medications",
  "height_weight",
]);
const PATIENT_SALUTATION_VALUES = new Set(["Mr.", "Mrs.", "Ms.", "Miss", "Mme.", "Prof."]);
const PRACTICE_MODE_ALIASES = {
  virtual: "virtual_only",
  physical: "in_person_only",
};
const PRACTICE_MODE_VALUES = new Set(["virtual_only", "in_person_only", "hybrid"]);
const DOCTOR_SALUTATION_VALUES = new Set(["Dr.", "Prof.", "Rn.", "Mr.", "Mme."]);

function getDoctorReadinessSessionCompletions() {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage?.getItem(DOCTOR_READINESS_SESSION_COMPLETE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function markDoctorReadinessStepComplete(step) {
  if (typeof window === "undefined" || !step) return;
  try {
    const completed = getDoctorReadinessSessionCompletions();
    completed.add(step);
    window.sessionStorage?.setItem(
      DOCTOR_READINESS_SESSION_COMPLETE_KEY,
      JSON.stringify(Array.from(completed)),
    );
  } catch {
    // Session progress is a UX hint only; persistence failures should not block saves.
  }
}

const FOLA_FLOW_STEPS = {
  patientEditIntro: {
    key: "patient-edit-intro-v2",
    role: "patient",
    messageKey: "fola.onboarding.patient.editIntro",
    message: "Let's build your profile so clinicians understand who they are supporting.",
    detailKey: "fola.onboarding.patient.editIntroSupport",
    detail: "Add your profile photo and complete/edit any sections below as needed. Once completed, Save Profile and follow next step.",
    cta: "",
    path: "",
  },
  patientProfile: {
    key: "patient-profile-ready-v2",
    role: "patient",
    messageKey: "fola.onboarding.patient.profileReady",
    message: "Review your profile",
    detailKey: "fola.onboarding.patient.profileReadySupport",
    detail: "This is how your profile information appears across AfriMed™. Review your details and update anything that needs attention before booking or attending appointments.",
    ctaKey: "fola.onboarding.patient.saveProfileCta",
    cta: "Save Profile",
    path: "/settings/patient/p/ehr",
  },
  patientHome: {
    key: "patient-home-guidance-v1",
    role: "patient",
    messageKey: "fola.onboarding.patient.homeAppointments",
    message: "Your upcoming and past appointments live here.",
    detailKey: "fola.onboarding.patient.homeAppointmentsSupport",
    detail: "To browse and book clinicians, tap Clinicians. You’ll land on a map. For best results, allow AfriMed™ to use your current location.",
    ctaKey: "fola.onboarding.patient.findCliniciansCta",
    cta: "Find clinicians",
    path: "/appointments/doctors",
  },
  patientAppointments: {
    key: "patient-appointments-first-time-v1",
    role: "patient",
    messageKey: "fola.onboarding.patient.appointments",
    message: "Appointment lists",
    detailKey: "fola.onboarding.patient.appointmentsSupport",
    detail: "This is where your daily appointment lists and booking snapshots live. Check your appointment details from time to time in here.",
    ctaKey: "fola.readiness.patient.nextStepCta",
    cta: "Next step",
    path: "/messages/fola",
  },
  doctorConsultancy: {
    key: "doctor-consultancy-edit-v2",
    role: "doctor",
    messageKey: "fola.onboarding.doctor.consultancyEdit",
    message: "Now, let’s add your availability and services so patients can book you.",
    detailKey: "fola.onboarding.doctor.consultancyEditSupport",
    detail: "Add as many services as you offer now before moving on to profile.",
    ctaKey: "fola.onboarding.doctor.consultancyEditCta",
    cta: "Let’s build your profile",
    path: "/settings/doctor/p/edit-intro",
  },
  doctorEditIntro: {
    key: "doctor-edit-intro-v1",
    role: "doctor",
    messageKey: "fola.onboarding.doctor.editIntro",
    message: "Let’s build your profile so patients are inspired to book your services.",
    detailKey: "fola.onboarding.doctor.editIntroSupport",
    detail:
      "Add your profile photo and complete/edit sections below. Add your signature in the signature section provided. Your signature is important and required to validate your prescriptions.",
    cta: "",
    path: "",
  },
  doctorPracticeMode: {
    key: "doctor-practice-mode-context-v1",
    role: "doctor",
    messageKey: "fola.onboarding.doctor.practiceModeContext",
    message: "Choose how patients can book you.",
    detailKey: "fola.onboarding.doctor.practiceModeContextSupport",
    detail:
      "Select whether you offer virtual visits, in-person visits, or both. This helps AfriMed™ guide patients correctly.",
    cta: "",
    path: "",
  },
  doctorServices: {
    key: "doctor-services-context-v1",
    role: "doctor",
    messageKey: "fola.onboarding.doctor.servicesContext",
    message: "Add the services patients can book.",
    detailKey: "fola.onboarding.doctor.servicesContextSupport",
    detail:
      "Services and pricing help patients understand what you offer before requesting an appointment.",
    cta: "",
    path: "",
  },
  doctorCover: {
    key: "doctor-cover-guidance-v1",
    role: "doctor",
    messageKey: "fola.onboarding.doctor.coverGuidance",
    message: "Add a cover picture to make your profile stand out.",
    detailKey: "fola.onboarding.doctor.coverGuidanceSupport",
    detail: "Tap the paperclip icon above. Use a true likeness image of your surgery or clinic.",
    cta: "",
    path: "",
    compact: true,
  },
  doctorProfile: {
    key: "doctor-profile-ready-v1",
    role: "doctor",
    messageKey: "fola.onboarding.doctor.profileReady",
    message: "Your consultancy is ready. You can now start receiving bookings.",
    ctaKey: "fola.onboarding.doctor.viewAppointmentsCta",
    cta: "View appointments",
    path: "/doctors/appointments",
  },
  doctorAppointments: {
    key: "doctor-appointments-first-time-v2",
    role: "doctor",
    messageKey: "fola.onboarding.doctor.appointments",
    message: "Appointment lists",
    detailKey: "fola.onboarding.doctor.appointmentsSupport",
    detail: "This is where your daily appointment lists and booking snapshots live. Check your appointment details from time to time in here.",
    cta: "",
    path: "",
  },
  doctorHome: {
    key: "doctor-home-appointments-guidance-v1",
    role: "doctor",
    messageKey: "fola.onboarding.doctor.homeAppointments",
    message: "Your upcoming and Pending appointments all live here.",
    detailKey: "fola.onboarding.doctor.homeAppointmentsSupport",
    detail:
      'Tap on "Upcoming" to keep up with your schedule. When you have appointments to approve, the bell at the top will have red badge alerts. Tap the bell to approve bookings. Tap View all to the far right to see all your appointments.',
    cta: "",
    path: "",
  },
};

const DOCTOR_CONTEXTUAL_PLACEMENTS = new Set([
  "doctorAppointments",
  "doctorCover",
  "doctorEditIntro",
  "doctorHome",
]);

function hasValue(value) {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some(hasValue);
  if (typeof value === "object") return Object.values(value).some(hasValue);
  if (typeof value === "string") {
    const trimmed = value.trim();
    return Boolean(trimmed && trimmed !== "[]" && trimmed !== "{}");
  }
  return true;
}

function normalizePracticeMode(value) {
  const raw = String(value || "").trim();
  const canonical = PRACTICE_MODE_ALIASES[raw] || raw;
  return PRACTICE_MODE_VALUES.has(canonical) ? canonical : "hybrid";
}

function normalizeExplicitPracticeMode(value) {
  const raw = String(value || "").trim();
  const canonical = PRACTICE_MODE_ALIASES[raw] || raw;
  return PRACTICE_MODE_VALUES.has(canonical) ? canonical : "";
}

function hasProfilePhoto(...rows) {
  return rows.some((row) =>
    hasValue(row?.profile_photo_path) ||
    hasValue(row?.profile_photo_storage_path) ||
    hasValue(row?.photo_path) ||
    hasValue(row?.avatar_path) ||
    hasValue(row?.profile_image_url) ||
    hasValue(row?.profile_photo_url) ||
    hasValue(row?.photo_url) ||
    hasValue(row?.avatar_url) ||
    hasValue(row?.avatar)
  );
}

function hasLocalDoctorProfilePhotoOverride() {
  if (!import.meta.env.DEV || typeof window === "undefined") return false;
  try {
    const host = String(window.location?.hostname || "");
    if (host !== "localhost" && host !== "127.0.0.1") return false;
    return hasValue(window.localStorage.getItem(DOCTOR_DEV_PHOTO_KEY));
  } catch {
    return false;
  }
}

function hasDoctorProfilePhoto(...rows) {
  return hasProfilePhoto(...rows) || hasLocalDoctorProfilePhotoOverride();
}

function hasDoctorSignature(...rows) {
  return rows.some((row) =>
    hasValue(row?.signature_path) ||
    hasValue(row?.signature_url) ||
    hasValue(row?.signature_storage_path) ||
    hasValue(row?.prescription_signature_path)
  );
}

function hasDoctorLanguages(row) {
  return hasValue(row?.languages_spoken) || hasValue(row?.languages);
}

function hasDoctorSpecialty(...rows) {
  return rows.some((row) =>
    hasValue(row?.primary_specialty) ||
    hasValue(row?.main_specialty) ||
    hasValue(row?.specialty) ||
    hasValue(row?.specialties) ||
    hasValue(row?.specialtySummary)
  );
}

function hasDoctorSubspecialty(...rows) {
  return rows.some((row) =>
    hasValue(row?.specialization) ||
    hasValue(row?.specializations) ||
    hasValue(row?.subspecialty) ||
    hasValue(row?.sub_specialty) ||
    hasValue(row?.subspecialties) ||
    hasValue(row?.sub_specialties) ||
    hasValue(row?.subSpecialties)
  );
}

function getDocumentEntries(...rows) {
  return rows.flatMap((row) => {
    const docs = row?.verification_documents || row?.documents || row?.uploads;
    if (Array.isArray(docs)) return docs;
    if (docs && typeof docs === "object") return Object.values(docs);
    return [];
  }).filter(Boolean);
}

function hasDoctorIdentity(...rows) {
  const identityKinds = new Set([
    "id",
    "identity",
    "identity_document",
    "id_passport",
    "national_id",
    "passport",
  ]);
  const hasIdentityField = rows.some((row) =>
    hasValue(row?.national_id_number) ||
    hasValue(row?.national_id) ||
    hasValue(row?.passport_number) ||
    hasValue(row?.id_number) ||
    hasValue(row?.identity_number) ||
    hasValue(row?.id_passport_number)
  );
  if (hasIdentityField) return true;
  return getDocumentEntries(...rows).some((doc) => {
    const kind = String(doc?.kind || doc?.type || doc?.document_type || "").trim().toLowerCase();
    return identityKinds.has(kind) && hasValue(doc);
  });
}

function hasDoctorRegistrationDetails(...rows) {
  return rows.some((row) =>
    hasValue(row?.medical_license_number) ||
    hasValue(row?.registration_number) ||
    hasValue(row?.professional_registration_number) ||
    hasValue(row?.license_number) ||
    hasValue(row?.licence_number) ||
    hasValue(row?.practice_number) ||
    hasValue(row?.registration_body) ||
    hasValue(row?.registration_country) ||
    hasValue(row?.register_country)
  );
}

function hasDoctorRegistrationUpload(...rows) {
  const registrationKinds = new Set([
    "medical_license",
    "license",
    "licence",
    "professional_registration",
    "registration",
    "registration_document",
  ]);
  const hasUploadField = rows.some((row) =>
    hasValue(row?.medical_license_url) ||
    hasValue(row?.medical_license_path) ||
    hasValue(row?.registration_document_url) ||
    hasValue(row?.registration_document_path) ||
    hasValue(row?.registration_upload_path)
  );
  if (hasUploadField) return true;
  return getDocumentEntries(...rows).some((doc) => {
    const kind = String(doc?.kind || doc?.type || doc?.document_type || "").trim().toLowerCase();
    return registrationKinds.has(kind) && hasValue(doc);
  });
}

function hasVerifiedPhone(...rows) {
  return rows.some((row) =>
    row?.phone_verified === true ||
    row?.is_phone_verified === true ||
    hasValue(row?.phone_verified_at) ||
    hasValue(row?.verified_phone_at)
  );
}

function hasCoverPhoto(row) {
  return hasValue(row?.cover_photo_path) || hasValue(row?.cover_image_url);
}

function hasPatientIntro(row, profile) {
  return (
    hasValue(row?.date_of_birth) &&
    (hasValue(row?.phone) || hasValue(profile?.phone)) &&
    (hasValue(profile?.full_name) || hasValue(profile?.first_name) || hasValue(profile?.last_name))
  );
}

function isGuidancePreviewMode() {
  return Boolean(import.meta.env.DEV || import.meta.env.MODE === "staging");
}

function normalizeFolaRole(value) {
  const role = String(value || "").toLowerCase().trim();
  return VALID_FOLA_ROLES.has(role) ? role : "";
}

function isFolaDebugEnabled() {
  return Boolean(import.meta.env.DEV || import.meta.env.MODE === "staging");
}

function isForcedGuidancePreview() {
  if (!isGuidancePreviewMode()) return false;
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("fola") === "preview" ||
      params.get("fola") === "show" ||
      params.get("showFola") === "1"
    );
  } catch {
    return false;
  }
}

function hasWorkingAvailability(row) {
  const candidates = [row?.availability, row?.availability_hours];
  return candidates.some((candidate) => {
    if (!candidate) return false;
    if (typeof candidate === "string") return candidate.trim() && candidate.trim() !== "{}";
    return hasValue(candidate);
  });
}

function isPhoneVerificationReadinessEnabled() {
  return String(import.meta.env.VITE_ENABLE_PHONE_VERIFICATION_READINESS || "")
    .trim()
    .toLowerCase() === "true";
}

function hasConsultancyName(rows) {
  return rows.some((row) => hasValue(row?.facility_name) || hasValue(row?.name));
}

function hasConsultancyAvailability(rows, doctorProfile) {
  return rows.some((row) => hasValue(row?.availability_text) || hasValue(row?.availability)) || hasWorkingAvailability(doctorProfile);
}

function hasConsultancyInsurance(rows) {
  return rows.some((row) => hasValue(row?.insurance_policy));
}

function hasConsultancyAgeGroups(rows) {
  return rows.some((row) => hasValue(row?.patients_age));
}

function hasDoctorIntro(row) {
  return hasValue(row?.about_you) || hasValue(row?.bio);
}

function hasActiveConsultancy(consultancies) {
  return Array.isArray(consultancies) && consultancies.some((row) => row?.is_active !== false);
}

function hasDoctorPricing({ doctorProfile, pricesCount, servicesCount = 0 }) {
  const fees = doctorProfile?.consultation_fees && typeof doctorProfile.consultation_fees === "object"
    ? doctorProfile.consultation_fees
    : {};
  return (
    Number(servicesCount || 0) > 0 ||
    Number(pricesCount || 0) > 0 ||
    Number(doctorProfile?.consultation_fee || 0) > 0 ||
    Number(doctorProfile?.evisit_price || 0) > 0 ||
    Number(fees?.evisit_price || fees?.evisitPrice || 0) > 0
  );
}

function hasDoctorPaymentMethods(doctorProfile) {
  const meta = parseDoctorPayoutMeta(doctorProfile?.payout_meta);
  return DOCTOR_PAYMENT_METHOD_OPTIONS.some((option) => meta?.[option.metaKey] === true);
}

function hasDoctorPaymentSetup(doctorProfile) {
  return doctorProfile?.canonical_payout_verified === true && hasDoctorPaymentMethods(doctorProfile);
}

function hasProfileName(...rows) {
  return rows.some((row) =>
    hasValue(row?.full_name) ||
    hasValue(row?.display_name) ||
    hasValue(row?.first_name) ||
    hasValue(row?.last_name)
  );
}

function hasContactPoint(...rows) {
  return rows.some((row) => hasValue(row?.phone) || hasValue(row?.email));
}

function hasMeaningfulPatientField(row) {
  return [
    row?.blood_type,
    row?.allergies,
    row?.medical_history,
    row?.medical_conditions,
    row?.current_medications,
    row?.national_id_number,
    row?.national_id,
    row?.profile_photo_path,
    row?.profile_photo_storage_path,
    row?.profile_image_url,
    row?.avatar_url,
  ].some(hasValue);
}

function hasPatientProfilePhoto(...rows) {
  return rows.some((row) =>
    hasValue(row?.profile_photo_path) ||
    hasValue(row?.profile_photo_storage_path) ||
    hasValue(row?.profile_image_url) ||
    hasValue(row?.avatar_url) ||
    hasValue(row?.photo_path) ||
    hasValue(row?.avatar_path)
  );
}

function hasMaterialDoctorSetup({ doctorProfile, consultancies, servicesCount, pricesCount, galleryCount }) {
  return (
    hasDoctorIntro(doctorProfile) ||
    hasConsultancyName(consultancies) ||
    hasConsultancyAvailability(consultancies, doctorProfile) ||
    Number(servicesCount || 0) > 0 ||
    Number(pricesCount || 0) > 0 ||
    Number(doctorProfile?.consultation_fee || 0) > 0 ||
    hasProfilePhoto(doctorProfile) ||
    hasCoverPhoto(doctorProfile) ||
    Number(galleryCount || 0) > 0
  );
}

function isPatientProfileMature({ patientProfile, patientEhrInfo, appointmentCount }) {
  const coreReadinessComplete = Boolean(
    hasValue(patientProfile?.date_of_birth) &&
      hasPatientIdentityDocument(patientProfile) &&
      hasPatientLanguages(patientProfile, patientEhrInfo) &&
      hasPatientAddress(patientProfile) &&
      hasValue(patientProfile?.blood_type) &&
      hasValue(patientProfile?.allergies) &&
      (hasValue(patientProfile?.medical_conditions) || hasValue(patientProfile?.medical_history)) &&
      hasValue(patientProfile?.current_medications) &&
      hasPatientHeightWeight(patientProfile, patientEhrInfo),
  );
  return coreReadinessComplete && Number(appointmentCount || 0) >= 2;
}

function normalizePatientSalutation(value) {
  const raw = String(value || "").trim();
  return PATIENT_SALUTATION_VALUES.has(raw) ? raw : "";
}

function resolvePatientAddressName({ patientProfile, profile, userId }) {
  const preview = loadPatientIntroPreview(userId);
  const salutation =
    normalizePatientSalutation(patientProfile?.salutation) ||
    normalizePatientSalutation(profile?.salutation) ||
    normalizePatientSalutation(profile?.user_metadata?.salutation) ||
    normalizePatientSalutation(preview?.salutation);
  const surname = resolveLastName(patientProfile, profile, preview);
  if (salutation && surname) return `${salutation} ${surname}`.trim();
  return resolveFirstName(patientProfile, null) || resolveFirstName(profile, null) || String(preview?.firstName || "").trim() || "there";
}

function hasPatientIdentityDocument(patientProfile = {}) {
  return hasValue(patientProfile?.national_id_number) || hasValue(patientProfile?.national_id);
}

function hasPatientLanguages(patientProfile = {}, ehrInfo = {}) {
  return hasValue(patientProfile?.languages) || hasValue(ehrInfo?.primary_language);
}

function hasPatientAddress(patientProfile = {}) {
  return hasValue(patientProfile?.street_address) && (hasValue(patientProfile?.city) || hasValue(patientProfile?.country));
}

function hasPatientHeightWeight(patientProfile = {}, ehrInfo = {}) {
  return (
    (hasValue(patientProfile?.height_cm) || hasValue(ehrInfo?.height_cm)) &&
    (hasValue(patientProfile?.weight_kg) || hasValue(ehrInfo?.weight_kg))
  );
}

function hasPatientEditIntroReady(patientProfile = {}, profile = {}) {
  return hasProfileName(patientProfile, profile) && hasPatientProfilePhoto(patientProfile, profile);
}

function getPatientProfileReviewSeen(userId) {
  if (typeof window === "undefined") return false;
  try {
    const key = `afrimed:fola:patient-profile-review-seen:${userId || "anonymous"}`;
    return window.localStorage?.getItem(key) === "1";
  } catch {
    return false;
  }
}

function markPatientProfileReviewSeen(userId) {
  if (typeof window === "undefined") return;
  try {
    const key = `afrimed:fola:patient-profile-review-seen:${userId || "anonymous"}`;
    window.localStorage?.setItem(key, "1");
  } catch {
    // This is orientation progress only; storage failures should not block the card.
  }
}

function makePatientReadinessStep({
  key,
  message,
  detail,
  cta,
  path,
  missingItem,
  dismissible = false,
  complete = false,
}) {
  return {
    key: `patient-readiness-${key}`,
    message,
    detail,
    cta,
    path,
    next_missing_item: missingItem,
    requirement: PATIENT_PERSISTED_READINESS_ITEMS.has(missingItem),
    complete,
    dismissible,
  };
}

function isPatientReadinessRequirementMissing({ missingItem, patientProfile, patientEhrInfo, profile }) {
  switch (missingItem) {
    case "languages":
      return !hasPatientLanguages(patientProfile, patientEhrInfo);
    case "address":
      return !hasPatientAddress(patientProfile);
    case "blood_type":
      return !hasValue(patientProfile?.blood_type);
    case "allergies":
      return !hasValue(patientProfile?.allergies);
    case "medical_conditions":
      return !(hasValue(patientProfile?.medical_conditions) || hasValue(patientProfile?.medical_history));
    case "medications":
      return !hasValue(patientProfile?.current_medications);
    case "height_weight":
      return !hasPatientHeightWeight(patientProfile, patientEhrInfo);
    case "profile_presentation":
      return !hasPatientEditIntroReady(patientProfile, profile);
    default:
      return false;
  }
}

function resolvePatientReadinessNextStep({
  patientProfile,
  profile,
  patientEhrInfo,
  currentPath,
  dismissed,
  userId,
  t,
}) {
  const addressName = resolvePatientAddressName({ patientProfile, profile, userId });
  const welcome = t("fola.readiness.patient.welcome", "Welcome {addressName}", { addressName });
  const route = String(currentPath || "").trim().replace(/\/+$/, "") || "/";
  const make = (config) => {
    const isCurrent = route === config.path || route.startsWith(`${config.path}/`);
    const step = makePatientReadinessStep({
      ...config,
      message: `${welcome}. ${config.message}`,
      cta: isCurrent && config.complete !== true ? "" : config.cta,
      path: isCurrent && config.complete !== true ? "" : config.path,
    });
    return step;
  };

  if (
    route === "/settings/patient" &&
    !(hasValue(patientProfile?.date_of_birth) && hasPatientIdentityDocument(patientProfile))
  ) {
    return makePatientReadinessStep({
      key: "identity-start",
      message: welcome,
      detail: t(
        "fola.readiness.patient.identityStartDetail",
        "Let's get your profile ready to book appointments. Start by confirming your identity.",
      ),
      cta: t("fola.readiness.patient.identityStartCta", "Identity"),
      path: "/settings/patient/p/edit-dob",
      missingItem: "identity",
    });
  }

  if (route === "/settings/patient/p/edit-dob" && !hasValue(patientProfile?.date_of_birth)) {
    return makePatientReadinessStep({
      key: "dob-current",
      message: t("fola.readiness.patient.dobCurrent", "Complete your date of birth."),
      detail: t(
        "fola.readiness.patient.dobCurrentDetail",
        "As part of your identity, please complete your date of birth below. Once completed, Save and follow next step.",
      ),
      cta: t("fola.readiness.patient.nextStepCta", "Next step"),
      path: "/settings/patient/p/edit-id-passport",
      missingItem: "date_of_birth",
    });
  }

  if (route === "/settings/patient/p/edit-id-passport" && !hasPatientIdentityDocument(patientProfile)) {
    return makePatientReadinessStep({
      key: "id-passport-current",
      message: t("fola.readiness.patient.idPassportCurrent", "Upload or capture your ID."),
      detail: t(
        "fola.readiness.patient.idPassportCurrentDetail",
        "Simply upload or capture an image of your government issued identity below. Once completed, your ID details will appear. Save and follow next step below.",
      ),
      cta: t("fola.readiness.patient.nextStepCta", "Next step"),
      path: "/settings/patient/p/languages",
      missingItem: "id_passport",
    });
  }

  if (
    (route === "/settings/patient/p/edit-dob" && hasValue(patientProfile?.date_of_birth)) ||
    (route === "/settings/patient/p/edit-id-passport" && hasPatientIdentityDocument(patientProfile))
  ) {
    return null;
  }

  const currentRouteSteps = {
    "/settings/patient/p/languages": {
      key: "languages-current",
      message: t("fola.readiness.patient.languagesCurrent", "Add Languages"),
      detail: t(
        "fola.readiness.patient.languagesCurrentDetail",
        "Add your preferred language and any other you speak. Once completed, Save and follow next step below.",
      ),
      path: "/settings/patient/p/edit-address",
      missingItem: "languages",
    },
    "/settings/patient/p/address": {
      key: "address-current",
      message: t("fola.readiness.patient.addressCurrent", "Complete your address below"),
      detail: t(
        "fola.readiness.patient.addressCurrentDetail",
        "To add your address automatically, click/tap on Use my current location. Alternatively complete it manually in the provided sections. Your address will be used to select services nearest to you in the App, only when you have explicitly allowed that action. Once completed, Save and follow next step.",
      ),
      path: "/settings/patient/p/blood-type",
      missingItem: "address",
    },
    "/settings/patient/p/edit-address": {
      key: "address-current",
      message: t("fola.readiness.patient.addressCurrent", "Complete your address below"),
      detail: t(
        "fola.readiness.patient.addressCurrentDetail",
        "To add your address automatically, click/tap on Use my current location. Alternatively complete it manually in the provided sections. Your address will be used to select services nearest to you in the App, only when you have explicitly allowed that action. Once completed, Save and follow next step.",
      ),
      path: "/settings/patient/p/blood-type",
      missingItem: "address",
    },
    "/settings/patient/p/blood-type": {
      key: "blood-type-current",
      message: t("fola.readiness.patient.bloodTypeCurrent", "Select your blood type"),
      detail: t(
        "fola.readiness.patient.bloodTypeCurrentDetail",
        "If known, select your blood type. If not known, simply select \"Other\". You can always return here to select it once it is known. Once completed, Save and follow next step.",
      ),
      path: "/settings/patient/p/allergies",
      missingItem: "blood_type",
    },
    "/settings/patient/p/allergies": {
      key: "allergies-current",
      message: t("fola.readiness.patient.allergiesCurrent", "Select your Allergies"),
      detail: t(
        "fola.readiness.patient.allergiesCurrentDetail",
        "If known, Search, select or + Add your Allergies. If not known, listed, simply select \"Other\". You can always return here to select or + Add it once it is known. Once completed, Save and follow next step.",
      ),
      path: "/settings/patient/p/conditions",
      missingItem: "allergies",
    },
    "/settings/patient/p/conditions": {
      key: "conditions-current",
      message: t("fola.readiness.patient.conditionsCurrent", "Select your Medical conditions"),
      detail: t(
        "fola.readiness.patient.conditionsCurrentDetail",
        "If known, Search, select or + Add your conditions. If not known, listed, simply select \"Other\". You can always return here to select or + Add it/them once known. Once completed, Save and follow next step.",
      ),
      path: "/settings/patient/p/medications",
      missingItem: "medical_conditions",
    },
    "/settings/patient/p/medications": {
      key: "medications-current",
      message: t("fola.readiness.patient.medicationsCurrent", "Select your Medications"),
      detail: t(
        "fola.readiness.patient.medicationsCurrentDetail",
        "If known, Search, select or + Add your medications. If not known, listed, simply select \"Other\". You can always return here to select or + Add it/them once known. Once completed, Save and follow next step.",
      ),
      path: "/settings/patient/p/height-weight",
      missingItem: "medications",
    },
    "/settings/patient/p/height-weight": {
      key: "height-weight-current",
      message: t("fola.readiness.patient.heightWeightCurrent", "Add your Height & Weight"),
      detail: t(
        "fola.readiness.patient.heightWeightCurrentDetail",
        "If known, add your height and weight below. If not known, you can always return here to add it/them once known. Once completed, Save and follow next step.",
      ),
      path: "/settings/patient/p/edit-intro",
      missingItem: "height_weight",
    },
    "/settings/patient/p/ehr": {
      key: "ehr-current",
      message: t("fola.readiness.patient.ehrCurrent", "Create or enter your password."),
      detail: t(
        "fola.readiness.patient.ehrCurrentDetail",
        "Create a new password if here for the first time. Otherwise enter it to unlock your record to enter. Once completed, simply follow next step. You'll always need to enter your password to unlock and enter your health record. Ensure your password is saved safely.",
      ),
      path: "/appointments",
      missingItem: "health_record_orientation",
    },
  };

  if (currentRouteSteps[route]) {
    const routeStep = currentRouteSteps[route];
    if (
      routeStep.missingItem === "health_record_orientation"
        ? !hasFirstUseOrientationPending(profile, "patient")
        : !isPatientReadinessRequirementMissing({
            missingItem: routeStep.missingItem,
            patientProfile,
            patientEhrInfo,
            profile,
          })
    ) {
      return null;
    }
    return makePatientReadinessStep({
      ...routeStep,
      cta: t("fola.readiness.patient.nextStepCta", "Next step"),
    });
  }

  if (route.startsWith("/appointments/prescriptions/")) {
    if (!hasFirstUseOrientationPending(profile, "patient")) return null;
    return makePatientReadinessStep({
      key: "prescriptions-current",
      message: t(
        "fola.readiness.patient.prescriptionsCurrent",
        "Your prescription from your most recent appointment is shown below.",
      ),
      detail: t(
        "fola.readiness.patient.prescriptionsCurrentDetail",
        "At the bottom, first select a pharmacy of your choice listed from closest to you. If cost appears, select Pay now to make payment and follow onscreen instructions. Select pick up to collect your medications. To have it delivered, select Delivery. If cost is not showing, Select request quote. You will be notified once a quote is ready.",
      ),
      cta: t("fola.readiness.patient.nextStepCta", "Next step"),
      path: "/appointments",
      missingItem: "prescriptions_orientation",
    });
  }

  const checks = [
    {
      key: "dob",
      ready: hasValue(patientProfile?.date_of_birth),
      path: "/settings/patient/p/edit-dob",
      missingItem: "date_of_birth",
      message: t("fola.readiness.patient.dob", "Add your date of birth."),
      detail: t("fola.readiness.patient.dobDetail", "This helps AfriMed™ show age-appropriate care details."),
      cta: t("fola.readiness.patient.dobCta", "Add date of birth"),
    },
    {
      key: "id-passport",
      ready: hasPatientIdentityDocument(patientProfile),
      path: "/settings/patient/p/edit-id-passport",
      missingItem: "id_passport",
      message: t("fola.readiness.patient.idPassport", "Add your ID or passport."),
      detail: t("fola.readiness.patient.idPassportDetail", "This helps keep your care profile linked to the right person."),
      cta: t("fola.readiness.patient.idPassportCta", "Add ID / Passport"),
    },
    {
      key: "languages",
      ready: hasPatientLanguages(patientProfile, patientEhrInfo),
      path: "/settings/patient/p/languages",
      missingItem: "languages",
      message: t("fola.readiness.patient.languages", "Choose your care languages."),
      detail: t("fola.readiness.patient.languagesDetail", "Clinicians can support you better when your preferred language is clear."),
      cta: t("fola.readiness.patient.languagesCta", "Choose languages"),
    },
    {
      key: "address",
      ready: hasPatientAddress(patientProfile),
      path: "/settings/patient/p/edit-address",
      missingItem: "address",
      message: t("fola.readiness.patient.address", "Add your address."),
      detail: t("fola.readiness.patient.addressDetail", "Your address helps with care context, pharmacy delivery, and appointment planning."),
      cta: t("fola.readiness.patient.addressCta", "Add address"),
    },
    {
      key: "blood-type",
      ready: hasValue(patientProfile?.blood_type),
      path: "/settings/patient/p/blood-type",
      missingItem: "blood_type",
      message: t("fola.readiness.patient.bloodType", "Add your blood type."),
      detail: t("fola.readiness.patient.bloodTypeDetail", "If you know it, save it so it is easy to find when needed."),
      cta: t("fola.readiness.patient.bloodTypeCta", "Add blood type"),
    },
    {
      key: "allergies",
      ready: hasValue(patientProfile?.allergies),
      path: "/settings/patient/p/allergies",
      missingItem: "allergies",
      message: t("fola.readiness.patient.allergies", "Add allergies."),
      detail: t("fola.readiness.patient.allergiesDetail", "Keep allergies visible before appointments and prescriptions."),
      cta: t("fola.readiness.patient.allergiesCta", "Add allergies"),
    },
    {
      key: "conditions",
      ready: hasValue(patientProfile?.medical_conditions) || hasValue(patientProfile?.medical_history),
      path: "/settings/patient/p/conditions",
      missingItem: "medical_conditions",
      message: t("fola.readiness.patient.conditions", "Add medical conditions."),
      detail: t("fola.readiness.patient.conditionsDetail", "Share ongoing conditions so clinicians see important context sooner."),
      cta: t("fola.readiness.patient.conditionsCta", "Add conditions"),
    },
    {
      key: "medications",
      ready: hasValue(patientProfile?.current_medications),
      path: "/settings/patient/p/medications",
      missingItem: "medications",
      message: t("fola.readiness.patient.medications", "Add medications."),
      detail: t("fola.readiness.patient.medicationsDetail", "Keep current medicines visible for safer consultations."),
      cta: t("fola.readiness.patient.medicationsCta", "Add medications"),
    },
    {
      key: "height-weight",
      ready: hasPatientHeightWeight(patientProfile, patientEhrInfo),
      path: "/settings/patient/p/height-weight",
      missingItem: "height_weight",
      message: t("fola.readiness.patient.heightWeight", "Add height and weight."),
      detail: t("fola.readiness.patient.heightWeightDetail", "These details can support medicine and care decisions."),
      cta: t("fola.readiness.patient.heightWeightCta", "Add height & weight"),
    },
    {
      key: "edit-intro",
      ready: hasPatientEditIntroReady(patientProfile, profile),
      path: "/settings/patient/p/edit-intro",
      missingItem: "profile_presentation",
      message: t(
        "fola.readiness.patient.editIntro",
        "Build your patient profile.",
      ),
      detail: t(
        "fola.readiness.patient.editIntroDetail",
        "Add your profile photo and review how your name, date of birth, contact details, and profile presentation appear before you review your profile.",
      ),
      cta: t("fola.readiness.patient.editIntroCta", "Edit Intro"),
    },
    {
      key: "profile-review",
      ready: getPatientProfileReviewSeen(userId),
      path: "/patient/profile",
      missingItem: "profile_review",
      message: t("fola.readiness.patient.profileReview", "Review your profile."),
      detail: t(
        "fola.readiness.patient.profileReviewDetail",
        "See how your profile information appears across AfriMed™ before you continue to My Health Record.",
      ),
      cta: t("fola.onboarding.patient.reviewProfileCta", "Review profile"),
    },
  ];

  const missing = checks.find((item) => !item.ready);
  if (missing) return make(missing);

  const orientation = [
    {
      key: "ehr",
      path: "/settings/patient/p/ehr",
      missingItem: "health_record_orientation",
      message: t("fola.readiness.patient.ehr", "Review My Health Record."),
      detail: t("fola.readiness.patient.ehrDetail", "Your health record keeps care information in one place."),
      cta: t("fola.readiness.patient.ehrCta", "Open health record"),
    },
    {
      key: "appointments",
      path: "/appointments",
      missingItem: "appointments_orientation",
      message: t("fola.readiness.patient.appointmentLists", "Appointment lists"),
      detail: t(
        "fola.readiness.patient.appointmentListsDetail",
        "This is where your daily appointment lists and booking snapshots live. Check your appointment details from time to time in here.",
      ),
      cta: t("fola.readiness.patient.appointmentListsCta", "Review appointments"),
    },
    {
      key: "messages",
      path: "/messages/fola",
      missingItem: "messages_fola_orientation",
      message: t("fola.readiness.patient.messagesFola", "Meet Fola"),
      detail: t(
        "fola.readiness.patient.messagesFolaDetail",
        "I’m Fola, your AfriMed™ AI Clinical Assistant. I can help you understand where things are, prepare for appointments, find prescriptions, and get support while using AfriMed™. I do not replace your clinician, but I can guide you through the app and help you get ready for care.",
      ),
      cta: t("fola.readiness.patient.messagesFolaCta", "Start with Fola"),
    },
  ];

  return orientation.map(make).find(Boolean) || null;
}

function isDoctorProfileMature({
  doctorProfile,
  profile,
  consultancies,
  servicesCount,
  pricesCount,
  eVisitEnabled,
}) {
  return Boolean(
    resolveDoctorReadinessNextStep({
      doctorProfile,
      profile,
      consultancies,
      servicesCount,
      pricesCount,
      eVisitEnabled,
    })?.clinician_ready
  );
}

function buildDoctorMaturitySignals({
  appointmentCount,
  prescriptionsCount,
  doctorProfile,
  profile,
  consultancies,
  servicesCount,
  pricesCount,
  eVisitEnabled,
}) {
  const explicitMode = normalizeExplicitPracticeMode(doctorProfile?.practice_mode);
  const mode = explicitMode || normalizePracticeMode(doctorProfile?.practice_mode);
  const readiness = resolveDoctorReadinessNextStep({
    doctorProfile,
    profile,
    consultancies,
    servicesCount,
    pricesCount,
    eVisitEnabled,
  });
  return {
    practice_mode: mode,
    practice_mode_selected: Boolean(explicitMode),
    appointments_count: Number(appointmentCount || 0),
    prescriptions_count: Number(prescriptionsCount || 0),
    identity_ready: hasDoctorIdentity(doctorProfile, profile),
    registration_details_ready: hasDoctorRegistrationDetails(doctorProfile, profile),
    registration_upload_ready: hasDoctorRegistrationUpload(doctorProfile, profile),
    languages_ready: hasDoctorLanguages(doctorProfile),
    specialty_ready: hasDoctorSpecialty(doctorProfile, profile),
    subspecialty_ready: hasDoctorSubspecialty(doctorProfile, profile),
    profile_picture_ready: hasDoctorProfilePhoto(doctorProfile, profile),
    active_consultancies_count: Array.isArray(consultancies)
      ? consultancies.filter((row) => row?.is_active !== false).length
      : 0,
    consultancies_required: mode !== "virtual_only",
    active_services_count: Number(servicesCount || 0),
    pricing_ready: hasDoctorPricing({ doctorProfile, pricesCount, servicesCount }),
    payout_ready: hasDoctorPaymentSetup(doctorProfile),
    payment_methods_ready: hasDoctorPaymentMethods(doctorProfile),
    stripe_account_linked: hasValue(doctorProfile?.stripe_account_id),
    availability_ready: hasWorkingAvailability(doctorProfile),
    e_visit_enabled: eVisitEnabled === true,
    e_visit_required: mode !== "in_person_only",
    phone_verified: hasVerifiedPhone(doctorProfile, profile),
    discovery_eligible: Boolean(readiness?.discovery_eligible),
    booking_eligible: Boolean(readiness?.booking_eligible),
    clinician_ready: Boolean(readiness?.clinician_ready),
    next_missing_item: readiness?.next_missing_item || "",
  };
}

function getWelcomeFields(role) {
  return String(role || "").toLowerCase().trim() === "doctor"
    ? {
        pending: "doctor_settings_welcome_pending",
        dismissedAt: "doctor_settings_welcome_dismissed_at",
      }
    : {
        pending: "patient_settings_welcome_pending",
        dismissedAt: "patient_settings_welcome_dismissed_at",
      };
}

function hasFirstUseOrientationPending(profile, role) {
  const fields = getWelcomeFields(role);
  return Boolean(profile?.[fields.pending] === true && !profile?.[fields.dismissedAt]);
}

function getSetupDismissalBoundary(profile, role) {
  const fields = getWelcomeFields(role);
  if (profile?.[fields.pending] === true && !profile?.[fields.dismissedAt]) {
    return Date.now();
  }

  const dismissedAt = Date.parse(String(profile?.[fields.dismissedAt] || ""));
  return Number.isFinite(dismissedAt) ? dismissedAt : 0;
}

function getStoredDismissals(userId, role, profile) {
  if (typeof window === "undefined") return {};
  try {
    const key = `afrimed:fola:onboarding:${role}:${userId || "anonymous"}`;
    const stored = JSON.parse(window.localStorage.getItem(key) || "{}") || {};
    const boundary = getSetupDismissalBoundary(profile, role);
    if (!boundary) return stored;
    return Object.fromEntries(
      Object.entries(stored).filter(([, dismissedAt]) => {
        const dismissedTime = Date.parse(String(dismissedAt || ""));
        return Number.isFinite(dismissedTime) && dismissedTime >= boundary;
      }),
    );
  } catch {
    return {};
  }
}

function clearStoredDismissals(userId, role) {
  if (typeof window === "undefined") return;
  try {
    const key = `afrimed:fola:onboarding:${role}:${userId || "anonymous"}`;
    window.localStorage.removeItem(key);
  } catch {
    // Reset support should never affect whether the screen can render.
  }
}

function shouldResetDismissals() {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("fola") === "reset" || params.get("resetFola") === "1";
  } catch {
    return false;
  }
}

async function loadPersistedDismissals(userId, role) {
  const uid = String(userId || "").trim();
  if (!uid) return {};
  try {
    const { data, error } = await supabase
      .from("fola_card_dismissals")
      .select("card_key,dismissed_at")
      .eq("user_id", uid)
      .eq("role", role);
    if (error || !Array.isArray(data)) return {};
    return Object.fromEntries(
      data
        .map((row) => [String(row?.card_key || "").trim(), row?.dismissed_at])
        .filter(([key, dismissedAt]) => key && dismissedAt),
    );
  } catch {
    return {};
  }
}

async function persistDismissal(userId, role, stepKey, dismissedAt) {
  const uid = String(userId || "").trim();
  const key = String(stepKey || "").trim();
  if (!uid || !key) return false;
  try {
    const { error } = await supabase
      .from("fola_card_dismissals")
      .upsert(
        {
          user_id: uid,
          role,
          card_key: key,
          dismissed_at: dismissedAt,
        },
        { onConflict: "user_id,role,card_key" },
      );
    if (error) throw error;
    return true;
  } catch (error) {
    console.warn("[FolaOnboardingCard] durable dismissal save failed:", error);
    // Local persistence remains as a fallback if the durable table is not available.
    return false;
  }
}

function setStoredDismissal(userId, role, stepKey) {
  if (typeof window === "undefined") return;
  try {
    const key = `afrimed:fola:onboarding:${role}:${userId || "anonymous"}`;
    const current = getStoredDismissals(userId, role);
    window.localStorage.setItem(
      key,
      JSON.stringify({
        ...current,
        [stepKey]: new Date().toISOString(),
      }),
    );
  } catch {
    // Dismissal is a nicety; never block the guidance card if storage is unavailable.
  }
}

function resolveFirstName(profile, user) {
  const explicit = String(profile?.first_name || "").trim();
  if (explicit) return explicit;
  const fullName = String(profile?.full_name || profile?.display_name || "").trim();
  if (fullName) return fullName.split(/\s+/)[0] || fullName;
  const email = String(user?.email || "").trim();
  if (email) return email.split("@")[0];
  return "";
}

function normalizeDoctorSalutation(value) {
  const raw = String(value || "").trim();
  return DOCTOR_SALUTATION_VALUES.has(raw) ? raw : "";
}

function resolveLastName(...rows) {
  for (const row of rows) {
    const explicit = String(row?.last_name || row?.family_name || "").trim();
    if (explicit) return explicit;
    const fullName = String(row?.full_name || row?.display_name || row?.name || "").trim();
    if (fullName) {
      const parts = fullName.split(/\s+/).filter(Boolean);
      if (parts.length > 1) return parts.slice(-1)[0];
    }
  }
  return "";
}

function formatFolaNamePart(value) {
  return String(value || "")
    .trim()
    .split(/([\s-]+)/)
    .map((part) => {
      if (!part || /^[\s-]+$/.test(part)) return part;
      return `${part.charAt(0).toLocaleUpperCase()}${part.slice(1)}`;
    })
    .join("");
}

function resolveDoctorAddressName({ profile, doctorProfile }) {
  const salutation =
    normalizeDoctorSalutation(doctorProfile?.salutation) ||
    normalizeDoctorSalutation(profile?.salutation) ||
    normalizeDoctorSalutation(profile?.professional_title) ||
    "Dr.";
  const surname = resolveLastName(doctorProfile, profile);
  if (surname) return `${salutation} ${formatFolaNamePart(surname)}`.trim();
  return salutation === "Dr." ? "Doctor" : salutation.replace(/\.$/, "");
}

function withFolaNamePrefix(role, addressedName, message) {
  const text = String(message || "").trim();
  if (!text) return text;
  const name = String(addressedName || "").trim();
  if (!name) return text;
  if (role === "doctor") return `Hello ${name}, ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
  return `Hi ${name}, ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
}

function hasClearDoctorSetup({
  doctorProfile,
  consultancies,
  servicesCount,
  pricesCount,
  galleryCount,
  appointmentCount,
}) {
  const hasMedia =
    hasProfilePhoto(doctorProfile) &&
    (hasCoverPhoto(doctorProfile) || Number(galleryCount || 0) > 0);

  return (
    hasConsultancyName(consultancies) &&
    hasConsultancyAvailability(consultancies, doctorProfile) &&
    Number(servicesCount || 0) > 0 &&
    (Number(pricesCount || 0) > 0 || Number(doctorProfile?.consultation_fee || 0) > 0) &&
    hasConsultancyInsurance(consultancies) &&
    hasConsultancyAgeGroups(consultancies) &&
    hasDoctorIntro(doctorProfile) &&
    hasMedia
  );
}

function makeReadinessStep({
  key,
  message,
  detail,
  cta,
  path,
  missingItem,
  complete = false,
  dismissible = false,
  discoveryEligible = false,
  bookingEligible = false,
  signals = {},
}) {
  const missing = Array.isArray(signals.missing) ? signals.missing : [];
  return {
    key: `${DOCTOR_READINESS_STEP_KEY}-${key}`,
    message,
    detail,
    cta,
    path,
    next_missing_item: missingItem,
    requirement: true,
    missing_requirements: missing,
    complete,
    dismissible,
    discovery_eligible: discoveryEligible,
    booking_eligible: bookingEligible,
    clinician_ready: complete,
    signals,
  };
}

function makeProfileReadinessReviewStep({ t, signals = {} }) {
  return makeReadinessStep({
    key: "profile-readiness-review",
    missingItem: "edit_intro",
    message: t("fola.readiness.doctor.profileReadinessReview", "Profile readiness review"),
    detail: t(
      "fola.readiness.doctor.profileReadinessReviewDetail",
      "Your core profile information is in place. Review your profile details, photo, signature, and availability before patients start booking.",
    ),
    cta: t("fola.readiness.doctor.profileReadinessReviewCta", "Review profile"),
    path: "/settings/doctor/p/edit-intro",
    discoveryEligible: Boolean(signals.discovery_eligible),
    bookingEligible: Boolean(signals.booking_eligible),
    signals,
  });
}

function shouldUseProfileReadinessReview(readinessStep) {
  const signals = readinessStep?.signals || {};
  return Boolean(
    readinessStep?.next_missing_item === "edit_intro" &&
    signals.discovery_eligible === true &&
    signals.availability_ready === true &&
    signals.booking_eligible !== true,
  );
}

export function resolveDoctorReadinessNextStep({
  userId = "",
  profile,
  user,
  doctorProfile,
  consultancies = [],
  servicesCount = 0,
  pricesCount = 0,
  eVisitEnabled = false,
  currentPath = "",
  t = (key, fallback) => fallback,
} = {}) {
  const addressName = resolveDoctorAddressName({ profile, doctorProfile, user });
  const explicitMode = normalizeExplicitPracticeMode(doctorProfile?.practice_mode);
  const mode = explicitMode || normalizePracticeMode(doctorProfile?.practice_mode);
  const identityReady = hasDoctorIdentity(doctorProfile, profile);
  const registrationDetailsReady = hasDoctorRegistrationDetails(doctorProfile, profile);
  const registrationUploadReady = hasDoctorRegistrationUpload(doctorProfile, profile);
  const languagesReady = hasDoctorLanguages(doctorProfile);
  const specialtyReady = hasDoctorSpecialty(doctorProfile, profile);
  const subspecialtyReady = hasDoctorSubspecialty(doctorProfile, profile);
  const practiceModeReady = Boolean(explicitMode);
  const servicesReady = Number(servicesCount || 0) > 0;
  const pricingReady = hasDoctorPricing({ doctorProfile, pricesCount, servicesCount });
  const payoutReady = hasDoctorPaymentSetup(doctorProfile);
  const consultancyReady = hasActiveConsultancy(consultancies) && hasConsultancyName(consultancies);
  const availabilityReady = hasWorkingAvailability(doctorProfile);
  // A profile photo is optional presentation. A prescription signature is the
  // only authoritative readiness requirement on this surface.
  const editIntroReady = hasDoctorSignature(doctorProfile, profile);
  const eVisitReady = eVisitEnabled === true;
  const consultancyRequired = mode !== "virtual_only";
  const eVisitRequired = mode === "virtual_only";
  const consultationReady = consultancyReady;
  const discoveryEligible =
    identityReady &&
    registrationDetailsReady &&
    registrationUploadReady &&
    languagesReady &&
    specialtyReady &&
    subspecialtyReady &&
    practiceModeReady &&
    servicesReady &&
    pricingReady &&
    eVisitReady &&
    consultationReady;
  const bookingEligible =
    discoveryEligible && availabilityReady && editIntroReady;

  const signals = {
    user_id: userId,
    mode,
    identity_ready: identityReady,
    registration_details_ready: registrationDetailsReady,
    registration_upload_ready: registrationUploadReady,
    practice_mode_selected: practiceModeReady,
    languages_ready: languagesReady,
    specialty_ready: specialtyReady,
    subspecialty_ready: subspecialtyReady,
    services_ready: servicesReady,
    pricing_ready: pricingReady,
    payout_ready: payoutReady,
    availability_ready: availabilityReady,
    edit_intro_ready: editIntroReady,
    consultancy_required: consultancyRequired,
    consultancy_ready: consultancyReady,
    e_visit_required: eVisitRequired,
    e_visit_ready: eVisitReady,
    consultation_ready: consultationReady,
    phone_verified: hasVerifiedPhone(doctorProfile, profile),
    phone_verification_actionable: isPhoneVerificationReadinessEnabled(),
    discovery_eligible: discoveryEligible,
    booking_eligible: bookingEligible,
  };
  signals.missing = [
    !identityReady && "identity",
    !registrationDetailsReady && "registration_details",
    !registrationUploadReady && "registration_upload",
    !languagesReady && "languages",
    !specialtyReady && "specialty",
    !subspecialtyReady && "subspecialty",
    !practiceModeReady && "practice_mode",
    !eVisitReady && "e_visit",
    !servicesReady && "services",
    !pricingReady && "pricing",
    !consultationReady && "consultancy",
    !availabilityReady && "availability",
    !editIntroReady && "edit_intro",
    !discoveryEligible && "discovery_eligibility",
    !bookingEligible && "booking_eligibility",
  ].filter(Boolean);

  const normalizedPath = String(currentPath || "").replace(/\/+$/, "") || "/";
  const sessionCompletedSteps = getDoctorReadinessSessionCompletions();
  const wasCompletedInSession = (step) => sessionCompletedSteps.has(step);
  const buildStep = ({
    key,
    missingItem,
    messageKey,
    message,
    detailKey,
    detail,
    ctaKey,
    cta,
    path,
    complete = false,
    dismissible = true,
  }) =>
    makeReadinessStep({
      key,
      missingItem,
      message: t(messageKey, message, { addressName }),
      detail: t(detailKey, detail),
      cta: cta ? t(ctaKey, cta) : "",
      path: cta ? path : "",
      complete,
      dismissible,
      discoveryEligible: complete || discoveryEligible,
      bookingEligible: complete || bookingEligible,
      signals,
    });

  const steps = {
    identity: {
      key: "identity",
      missingItem: "identity",
      messageKey: "fola.readiness.doctor.identity",
      message: "Welcome {addressName}",
      detailKey: "fola.readiness.doctor.identityDetail",
      detail: "Let’s get your profile ready so patients can find and book you. Start by confirming your identity.",
      ctaKey: "fola.readiness.doctor.identityCta",
      cta: "Identity",
      path: "/settings/doctor/p/edit-dob",
      ready: identityReady,
    },
    registrationDetails: {
      key: "registration-details",
      missingItem: "registration_details",
      messageKey: "fola.readiness.doctor.registrationDetails",
      message: "Complete your registration details",
      detailKey: "fola.readiness.doctor.registrationDetailsDetail",
      detail: "Complete your professional registration information below.",
      ctaKey: "fola.readiness.doctor.registrationDetailsCta",
      cta: "Registration details",
      path: "/settings/doctor/p/registration-details",
      ready: registrationDetailsReady,
    },
    registrationUpload: {
      key: "registration-upload",
      missingItem: "registration_upload",
      messageKey: "fola.readiness.doctor.registrationUpload",
      message: "Upload your registration document",
      detailKey: "fola.readiness.doctor.registrationUploadDetail",
      detail: "Upload a clear copy of your professional registration document below.",
      ctaKey: "fola.readiness.doctor.registrationUploadCta",
      cta: "Registration document",
      path: "/settings/doctor/p/upload-registration",
      ready: registrationUploadReady,
    },
    languages: {
      key: "languages",
      missingItem: "languages",
      messageKey: "fola.readiness.doctor.languages",
      message: "Add languages",
      detailKey: "fola.readiness.doctor.languagesDetail",
      detail: "Add your preferred language and any other languages you speak.",
      ctaKey: "fola.readiness.doctor.languagesCta",
      cta: "Languages",
      path: "/settings/doctor/p/languages",
      ready: languagesReady,
    },
    specialty: {
      key: "specialty",
      missingItem: "specialty",
      messageKey: "fola.readiness.doctor.specialty",
      message: "Select your specialty",
      detailKey: "fola.readiness.doctor.specialtyDetail",
      detail: "Select or add all specialties you practise.",
      ctaKey: "fola.readiness.doctor.specialtyCta",
      cta: "Specialty",
      path: "/settings/doctor/p/specialty",
      ready: specialtyReady,
    },
    subspecialty: {
      key: "subspecialty",
      missingItem: "subspecialty",
      messageKey: "fola.readiness.doctor.subspecialty",
      message: "Select your subspecialties",
      detailKey: "fola.readiness.doctor.subspecialtyDetail",
      detail: "Select or add all subspecialties you practise.",
      ctaKey: "fola.readiness.doctor.subspecialtyCta",
      cta: "Subspecialties",
      path: "/settings/doctor/p/subspecialty",
      ready: subspecialtyReady,
    },
    practiceMode: {
      key: "practice-mode",
      missingItem: "practice_mode",
      messageKey: "fola.readiness.doctor.practiceMode",
      message: "Choose your practice type",
      detailKey: "fola.readiness.doctor.practiceModeDetail",
      detail: "Select how patients can consult with you on AfriMed™.",
      ctaKey: "fola.readiness.doctor.practiceModeCta",
      cta: "Practice type",
      path: "/settings/doctor/p/practice-mode",
      ready: practiceModeReady,
    },
    eVisit: {
      key: "e-visit",
      missingItem: "e_visit",
      messageKey: "fola.readiness.doctor.evisit",
      message: "Enable virtual consultations",
      detailKey: "fola.readiness.doctor.evisitDetail",
      detail:
        "Toggle on Virtual consultations below to allow patients to book live online consultations with you. Set your virtual consultation price at the bottom. Once complete, continue to the next step.",
      ctaKey: "fola.readiness.doctor.evisitCta",
      cta: "Virtual consultations",
      path: "/settings/doctor/p/evisit",
      ready: eVisitReady,
    },
    services: {
      key: "services",
      missingItem: "services",
      messageKey: "fola.readiness.doctor.services",
      message: "Add services and prices",
      detailKey: "fola.readiness.doctor.servicesDetail",
      detail: "Add all services you offer including duration and pricing.",
      ctaKey: "fola.readiness.doctor.servicesCta",
      cta: "Services & pricing",
      path: "/settings/doctor/p/services",
      ready: servicesReady && pricingReady,
    },
    payout: {
      key: "payout",
      missingItem: "payout_setup",
      messageKey: "fola.readiness.doctor.payout",
      message: "Receive payouts for your services",
      detailKey: "fola.readiness.doctor.payoutDetail",
      detail:
        "Use Manage payout to verify your receiving bank account, then select and save the payment methods you accept.",
      ctaKey: "fola.readiness.doctor.payoutCta",
      cta: "Payout preferences",
      path: "/settings/doctor/p/payout-preferences",
      ready: payoutReady,
    },
    consultation: {
      key: "consultancy",
      missingItem: "consultancy",
      messageKey: "fola.readiness.doctor.consultation",
      message: "Add your physical consultancies",
      detailKey: "fola.readiness.doctor.consultationDetail",
      detail: "These are physical addresses where patients can book in-person appointments. Complete every section below.",
      ctaKey: "fola.readiness.doctor.consultancyCta",
      cta: "Consultancy",
      path: "/settings/doctor/p/consultancies",
      ready: consultationReady,
    },
    availability: {
      key: "availability",
      missingItem: "availability",
      messageKey: "fola.readiness.doctor.availability",
      message: "Add your availability",
      detailKey: "fola.readiness.doctor.availabilityDetail",
      detail: "Carefully add your availability so patients know when they can request appointments.",
      ctaKey: "fola.readiness.doctor.availabilityCta",
      cta: "Availability",
      path: "/settings/doctor/availability",
      ready: availabilityReady,
    },
    editIntro: {
      key: "edit-intro",
      missingItem: "edit_intro",
      messageKey: "fola.readiness.doctor.editIntro",
      message: "Add your prescription signature",
      detailKey: "fola.readiness.doctor.editIntroDetail",
      detail: "Add your signature so AfriMed™ can validate prescriptions you issue.",
      ctaKey: "fola.readiness.doctor.editIntroCta",
      cta: "Add signature",
      path: "/settings/doctor/p/edit-intro",
      ready: editIntroReady,
    },
    discoveryReview: {
      key: "discovery-review",
      missingItem: "discovery_eligibility",
      messageKey: "fola.readiness.doctor.discoveryReview",
      message: "Excellent. Let's make your profile discoverable.",
      detailKey: "fola.readiness.doctor.discoveryReviewDetail",
      detail: "Your core profile is complete. AfriMed™ can now evaluate whether patients can discover and request appointments with you.",
      ctaKey: "fola.readiness.doctor.discoveryReviewCta",
      cta: "Review profile",
      path: "/doctor/profile",
      ready: discoveryEligible && availabilityReady && editIntroReady,
    },
    ready: {
      key: "ready",
      missingItem: "",
      messageKey: "fola.readiness.doctor.ready",
      message: "Your profile is ready.",
      detailKey: "fola.readiness.doctor.readyDetail",
      detail: "Patients can now discover your profile and request appointments where available.",
      ctaKey: "fola.readiness.doctor.readyCta",
      cta: "View profile",
      path: "/doctor/profile",
      ready: true,
    },
  };

  const nextStepCta = "Next step";
  const taskSupportSteps = {
    identity: {
      ...steps.identity,
      key: "identity-current",
      messageKey: "fola.readiness.doctor.identityCurrent",
      message: "Upload or capture your ID",
      detailKey: "fola.readiness.doctor.identityCurrentDetail",
      detail:
        "Upload or capture a clear image of your government-issued identity document. Once completed, Save and follow the next step below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/registration-details",
    },
    registrationDetails: {
      ...steps.registrationDetails,
      key: "registration-details-current",
      messageKey: "fola.readiness.doctor.registrationDetailsCurrent",
      message: "Complete your registration details",
      detailKey: "fola.readiness.doctor.registrationDetailsCurrentDetail",
      detail: "Once completed, Save and follow the next step below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/upload-registration",
    },
    registrationUpload: {
      ...steps.registrationUpload,
      key: "registration-upload-current",
      messageKey: "fola.readiness.doctor.registrationUploadCurrent",
      message: "Upload your document",
      detailKey: "fola.readiness.doctor.registrationUploadCurrentDetail",
      detail: "Once completed, Save and follow the next step below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/languages",
    },
    languages: {
      ...steps.languages,
      key: "languages-current",
      messageKey: "fola.readiness.doctor.languagesCurrent",
      message: "Add languages",
      detailKey: "fola.readiness.doctor.languagesCurrentDetail",
      detail: "Add your preferred language and any other languages you speak. Once completed, Save and follow the next step below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/specialty",
    },
    specialty: {
      ...steps.specialty,
      key: "specialty-current",
      messageKey: "fola.readiness.doctor.specialtyCurrent",
      message: "Select your specialty",
      detailKey: "fola.readiness.doctor.specialtyCurrentDetail",
      detail: "Select and/or + Add as many specialties as you have. Once completed, Save and follow the next step below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/subspecialty",
    },
    subspecialty: {
      ...steps.subspecialty,
      key: "subspecialty-current",
      messageKey: "fola.readiness.doctor.subspecialtyCurrent",
      message: "Select your subspecialties",
      detailKey: "fola.readiness.doctor.subspecialtyCurrentDetail",
      detail: "Select and/or + Add as many subspecialties as you have. Once completed, Save and follow the next step below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/practice-mode",
    },
    practiceMode: {
      ...steps.practiceMode,
      key: "practice-mode-current",
      messageKey: "fola.readiness.doctor.practiceModeCurrent",
      message: "Choose your practice type",
      detailKey: "fola.readiness.doctor.practiceModeCurrentDetail",
      detail: "Once completed, Save and follow the next step below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/evisit",
    },
    eVisit: {
      ...steps.eVisit,
      key: "e-visit-current",
      missingItem: "e_visit",
      messageKey: "fola.readiness.doctor.evisitCurrent",
      message: "Enable virtual consultations",
      detailKey: "fola.readiness.doctor.evisitCurrentDetail",
      detail:
        "Toggle on Virtual consultations below to allow patients to book live online consultations with you. Set your virtual consultation price at the bottom. Once complete, continue to the next step.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/services",
    },
    services: {
      ...steps.services,
      key: "services-current",
      messageKey: "fola.readiness.doctor.servicesCurrent",
      message: "Add services and prices",
      detailKey: "fola.readiness.doctor.servicesCurrentDetail",
      detail: "Add as many services as you offer. Include their duration and prices. Once completed, Save and follow the next step below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/consultancies",
    },
    payout: {
      ...steps.payout,
      key: "payout-current",
      messageKey: "fola.readiness.doctor.payoutCurrent",
      message: "Receive payouts for your services",
      detailKey: "fola.readiness.doctor.payoutCurrentDetail",
      detail:
        "Use Manage payout to verify your receiving bank account, then select and save the payment methods you accept.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/consultancies",
    },
    consultation: {
      ...steps.consultation,
      key: "consultancy-current",
      messageKey: "fola.readiness.doctor.consultancyCurrent",
      message: "Add your physical consultancies",
      detailKey: "fola.readiness.doctor.consultancyCurrentDetail",
      detail: "These are physical addresses where patients can book in-person appointments. Add as many as you have. Complete every section indicated below.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/availability",
    },
    availability: {
      ...steps.availability,
      key: "availability-current",
      messageKey: "fola.readiness.doctor.availabilityCurrent",
      message: "Now add your availability",
      detailKey: "fola.readiness.doctor.availabilityCurrentDetail",
      detail:
        "Carefully add your availability so patients know when to book you. Once completed, Save availability at the bottom of the page. Your core readiness steps are complete.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/edit-intro",
    },
    editIntro: {
      ...steps.editIntro,
      key: "edit-intro-current",
      messageKey: "fola.readiness.doctor.editIntroCurrent",
      message: "Let’s build your profile so patients are inspired to book your services.",
      detailKey: "fola.readiness.doctor.editIntroCurrentDetail",
      detail:
        "Add your profile photo and complete/edit sections below. Add your signature in the signature section provided. Your signature is important and required to validate your prescriptions.",
      ctaKey: "fola.readiness.doctor.discoveryReviewCta",
      cta: "Review profile",
      path: "/doctor/profile",
    },
    editDob: {
      key: "edit-dob-current",
      missingItem: "date_of_birth",
      messageKey: "fola.readiness.doctor.editDobCurrent",
      message: "Complete your date of birth.",
      detailKey: "fola.readiness.doctor.editDobCurrentDetail",
      detail: "As part of your identity, please complete your date of birth below. Once completed, Save and follow next step.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/edit-id-passport",
      ready: hasValue(doctorProfile?.date_of_birth) || hasValue(profile?.date_of_birth),
    },
  };

  const taskCompleteSteps = {
    identity: {
      ...steps.registrationDetails,
      key: "identity-complete",
      messageKey: "fola.readiness.doctor.identityComplete",
      message: "Identity received",
      detailKey: "fola.readiness.doctor.identityCompleteDetail",
      detail: "Next, add your professional registration details.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/registration-details",
    },
    registrationDetails: {
      ...steps.registrationUpload,
      key: "registration-details-complete",
      messageKey: "fola.readiness.doctor.registrationDetailsComplete",
      message: "Registration details saved",
      detailKey: "fola.readiness.doctor.registrationDetailsCompleteDetail",
      detail: "Next, upload your registration document.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/upload-registration",
    },
    registrationUpload: {
      ...steps.languages,
      key: "registration-upload-complete",
      messageKey: "fola.readiness.doctor.registrationUploadComplete",
      message: "Registration document received",
      detailKey: "fola.readiness.doctor.registrationUploadCompleteDetail",
      detail:
        "Next, choose your preferred working language and any other languages you speak.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/languages",
    },
    languages: {
      ...steps.specialty,
      key: "languages-complete",
      messageKey: "fola.readiness.doctor.languagesComplete",
      message: "Languages saved",
      detailKey: "fola.readiness.doctor.languagesCompleteDetail",
      detail:
        "Next, choose your specialty so patients understand the type of care you provide.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/specialty",
    },
    specialty: {
      ...steps.subspecialty,
      key: "specialty-complete",
      messageKey: "fola.readiness.doctor.specialtyComplete",
      message: "Specialty saved",
      detailKey: "fola.readiness.doctor.specialtyCompleteDetail",
      detail:
        "Next, add your subspecialties so patients can find your specific expertise.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/subspecialty",
    },
    subspecialty: {
      ...steps.practiceMode,
      key: "subspecialty-complete",
      messageKey: "fola.readiness.doctor.subspecialtyComplete",
      message: "Subspecialties saved",
      detailKey: "fola.readiness.doctor.subspecialtyCompleteDetail",
      detail: "Next, choose how patients can consult with you on AfriMed™.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/practice-mode",
    },
    practiceMode: {
      ...steps.eVisit,
      key: "practice-mode-complete",
      messageKey: "fola.readiness.doctor.practiceModeComplete",
      message: "Practice type saved",
      detailKey: "fola.readiness.doctor.practiceModeCompleteDetail",
      detail: "Next, enable virtual consultations.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/evisit",
    },
    eVisit: {
      ...steps.services,
      key: "e-visit-complete",
      messageKey: "fola.readiness.doctor.evisitComplete",
      message: "Virtual consultations saved",
      detailKey: "fola.readiness.doctor.evisitCompleteDetail",
      detail: "Next, add the services and prices patients can book with you.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/services",
    },
    services: {
      ...steps.consultation,
      key: "services-complete",
      messageKey: "fola.readiness.doctor.servicesComplete",
      message: "Services saved",
      detailKey: "fola.readiness.doctor.servicesCompleteDetail",
      detail: "Next, add your physical consultancies.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/consultancies",
    },
    payout: {
      ...steps.consultation,
      key: "payout-complete",
      messageKey: "fola.readiness.doctor.payoutComplete",
      message: "Payout setup saved",
      detailKey: "fola.readiness.doctor.payoutCompleteDetail",
      detail: "Next, tell patients where they can consult with you.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/p/consultancies",
    },
    consultation: {
      ...steps.availability,
      key: "consultancy-complete",
      messageKey: "fola.readiness.doctor.consultancyComplete",
      message: "Consultancy saved",
      detailKey: "fola.readiness.doctor.consultancyCompleteDetail",
      detail:
        "Next, set your availability. Patients can only request appointments when AfriMed™ knows when you are available.",
      ctaKey: "fola.readiness.doctor.nextStepCta",
      cta: nextStepCta,
      path: "/settings/doctor/availability",
    },
    availability: {
      ...steps.editIntro,
      key: "availability-complete",
      messageKey: "fola.readiness.doctor.availabilityComplete",
      message: "Availability saved",
      detailKey: "fola.readiness.doctor.availabilityCompleteDetail",
      detail:
        "Your core readiness steps are complete. Review how your profile appears to patients.",
      ctaKey: "fola.readiness.doctor.discoveryReviewCta",
      cta: "Review profile",
      path: "/doctor/profile",
    },
    editIntro: {
      ...steps.ready,
      key: "edit-intro-complete",
      messageKey: "fola.readiness.doctor.ready",
      message: "Your profile is ready.",
      detailKey: "fola.readiness.doctor.readyDetail",
      detail: "Patients can now discover your profile and request appointments where available.",
      ctaKey: "fola.readiness.doctor.readyCta",
      cta: "View profile",
      path: "/doctor/profile",
      complete: true,
    },
  };

  const orderedSteps = [
    steps.identity,
    steps.registrationDetails,
    steps.registrationUpload,
    steps.languages,
    steps.specialty,
    steps.subspecialty,
    steps.practiceMode,
    steps.eVisit,
    steps.services,
    steps.consultation,
    steps.availability,
    steps.editIntro,
  ];

  const routeReadinessSteps = {
    "/settings/doctor/p/id-passport": ["identity", taskSupportSteps.identity, taskCompleteSteps.identity],
    "/settings/doctor/p/edit-id-passport": ["identity", taskSupportSteps.identity, taskCompleteSteps.identity],
    "/settings/doctor/p/registration-details": ["registrationDetails", taskSupportSteps.registrationDetails, taskCompleteSteps.registrationDetails],
    "/settings/doctor/p/reg-details": ["registrationDetails", taskSupportSteps.registrationDetails, taskCompleteSteps.registrationDetails],
    "/settings/doctor/p/upload-registration": ["registrationUpload", taskSupportSteps.registrationUpload, taskCompleteSteps.registrationUpload],
    "/settings/doctor/p/reg-upload": ["registrationUpload", taskSupportSteps.registrationUpload, taskCompleteSteps.registrationUpload],
    "/settings/doctor/p/languages": ["languages", taskSupportSteps.languages, taskCompleteSteps.languages],
    "/settings/doctor/p/specialty": ["specialty", taskSupportSteps.specialty, taskCompleteSteps.specialty],
    "/settings/doctor/p/settings-specialty-edit": ["specialty", taskSupportSteps.specialty, taskCompleteSteps.specialty],
    "/settings/doctor/p/subspecialty": ["subspecialty", taskSupportSteps.subspecialty, taskCompleteSteps.subspecialty],
    "/settings/doctor/p/settings-subspecialty-edit": ["subspecialty", taskSupportSteps.subspecialty, taskCompleteSteps.subspecialty],
    "/settings/doctor/p/practice-mode": ["practiceMode", taskSupportSteps.practiceMode, taskCompleteSteps.practiceMode],
    "/settings/doctor/p/evisit": ["eVisit", taskSupportSteps.eVisit, taskCompleteSteps.eVisit],
    "/settings/doctor/p/e-visit": ["eVisit", taskSupportSteps.eVisit, taskCompleteSteps.eVisit],
    "/settings/doctor/p/services": ["services", taskSupportSteps.services, taskCompleteSteps.services],
    "/settings/doctor/p/payout-preferences": ["payout", taskSupportSteps.payout, taskCompleteSteps.payout],
    "/settings/doctor/p/consultancies": ["consultation", taskSupportSteps.consultation, taskCompleteSteps.consultation],
    "/settings/doctor/p/consultancies/edit": ["consultation", taskSupportSteps.consultation, taskCompleteSteps.consultation],
    "/settings/doctor/availability": ["availability", taskSupportSteps.availability, taskCompleteSteps.availability],
    "/settings/doctor/p/availability": ["availability", taskSupportSteps.availability, taskCompleteSteps.availability],
    "/settings/doctor/p/edit-intro": ["editIntro", taskSupportSteps.editIntro, taskCompleteSteps.editIntro],
    "/settings/doctor/p/edit-dob": ["editDob", taskSupportSteps.editDob, taskSupportSteps.identity],
  };

  const supportStepByMissingKey = {
    identity: taskSupportSteps.identity,
    "registration-details": taskSupportSteps.registrationDetails,
    "registration-upload": taskSupportSteps.registrationUpload,
    languages: taskSupportSteps.languages,
    specialty: taskSupportSteps.specialty,
    subspecialty: taskSupportSteps.subspecialty,
    "practice-mode": taskSupportSteps.practiceMode,
    "e-visit": taskSupportSteps.eVisit,
    services: taskSupportSteps.services,
    payout: taskSupportSteps.payout,
    consultancy: taskSupportSteps.consultation,
    availability: taskSupportSteps.availability,
    "edit-intro": taskSupportSteps.editIntro,
  };

  if (routeReadinessSteps[normalizedPath]) {
    const [completionKey, currentStep] = routeReadinessSteps[normalizedPath];
    const requirement = steps[completionKey] || null;
    return requirement?.ready ? null : buildStep(currentStep);
  }

  const firstMissingStep = orderedSteps.find((step) => !step.ready);

  if (normalizedPath === "/settings/doctor") {
    return firstMissingStep ? buildStep({ ...firstMissingStep, dismissible: false }) : null;
  }

  if (firstMissingStep) {
    if (
      ["/home/doctor", "/doctors/appointments", "/doctor/profile"].includes(normalizedPath) &&
      firstMissingStep.key === "edit-intro" &&
      discoveryEligible &&
      availabilityReady
    ) {
      return makeProfileReadinessReviewStep({ t, signals });
    }
    return buildStep(firstMissingStep);
  }

  if (!bookingEligible) {
    return makeReadinessStep({
      key: "booking-review",
      missingItem: "booking_eligibility",
      message: t("fola.readiness.doctor.discoveryReview", "Excellent. Let's make your profile discoverable."),
      detail: t(
        "fola.readiness.doctor.discoveryReviewDetail",
        "Your core profile is complete. AfriMed™ can now evaluate whether patients can discover and request appointments with you.",
      ),
      cta: t("fola.readiness.doctor.discoveryReviewCta", "Review profile"),
      path: "/doctor/profile",
      discoveryEligible,
      bookingEligible,
      signals,
    });
  }

  return buildStep({ ...steps.ready, complete: true });
}

function buildDoctorStep({
  profile,
  user,
  doctorProfile,
  consultancies,
  servicesCount,
  pricesCount,
  galleryCount,
  appointmentCount,
  prescriptionsCount,
  eVisitEnabled,
  currentPath,
  t,
}) {
  const readiness = resolveDoctorReadinessNextStep({
    profile,
    user,
    doctorProfile,
    consultancies,
    servicesCount,
    pricesCount,
    eVisitEnabled,
    currentPath,
    t,
  });

  return readiness;
}

function buildPlacementStep({ placement, role, profile, user, patientProfile, t }) {
  const config = FOLA_FLOW_STEPS[placement];
  if (!config) return null;
  const resolvedRole = config.role || role;
  if (resolvedRole !== role) return null;
  const addressedName = resolvedRole === "doctor"
    ? resolveDoctorAddressName({ profile, doctorProfile: null })
    : resolvePatientAddressName({ patientProfile, profile, userId: user?.id || "" });
  const message = t(config.messageKey, config.message);
  const patientEditIntroComplete = Boolean(
    placement === "patientEditIntro" &&
      hasProfileName(patientProfile, profile, user) &&
      hasPatientProfilePhoto(patientProfile, profile),
  );
  if (placement === "patientEditIntro" && patientEditIntroComplete) return null;
  return {
    key: config.key,
    message: config.includeNamePrefix ? withFolaNamePrefix(resolvedRole, addressedName, message) : message,
    detail: config.detailKey ? t(config.detailKey, config.detail || "") : "",
    cta: config.ctaKey
        ? t(config.ctaKey, config.cta)
        : config.cta || "",
    path: config.path,
    compact: Boolean(config.compact),
  };
}

function buildDoctorPlacementStep({ placement, role, profile, user, doctorProfile, readinessStep, t }) {
  // Profiles, dashboards, appointments, and field-edit surfaces already
  // communicate their own context. Route presence and optional imagery are
  // not evidence of an unfinished requirement, so these placements stay quiet.
  return null;
}

function isCurrentRequirementSurface(currentPath, targetPath) {
  const current = String(currentPath || "").replace(/\/+$/, "") || "/";
  const target = String(targetPath || "").replace(/\/+$/, "") || "/";
  return Boolean(target && target !== "/" && current === target);
}

export function isTruthGatedSmartCardStep(step, currentPath) {
  return Boolean(
    step?.requirement === true &&
      step?.complete !== true &&
      step?.next_missing_item &&
      step?.cta &&
      step?.path &&
      !isCurrentRequirementSurface(currentPath, step.path),
  );
}

function isMatureForRole({
  role,
  patientProfile,
  patientEhrInfo,
  doctorProfile,
  profile,
  user,
  consultancies,
  servicesCount,
  pricesCount,
  galleryCount,
  appointmentCount,
  prescriptionsCount,
  eVisitEnabled,
}) {
  if (role === "doctor") {
    return isDoctorProfileMature({
      doctorProfile,
      profile,
      user,
      consultancies,
      servicesCount,
      pricesCount,
      galleryCount,
      appointmentCount,
      prescriptionsCount,
      eVisitEnabled,
    });
  }
  return isPatientProfileMature({ patientProfile, patientEhrInfo, profile, user, appointmentCount });
}

export default function FolaOnboardingCard({
  role = "patient",
  placement = "settings",
  onCtaClick,
  matureOverride,
  className = "",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { t } = useLanguage();
  const user = auth.user || null;
  const profile = auth.profile || null;
  const effectiveRole = normalizeFolaRole(profile?.role) || normalizeFolaRole(role) || "patient";
  const userId = String(user?.id || profile?.user_id || "").trim();
  const [state, setState] = React.useState({
    loading: true,
    patientProfile: null,
    patientEhrInfo: null,
    doctorProfile: null,
    consultancies: [],
    servicesCount: 0,
    pricesCount: 0,
    prescriptionsCount: 0,
    appointmentCount: 0,
    galleryCount: 0,
    eVisitEnabled: false,
    dismissed: {},
  });
  const [entered, setEntered] = React.useState(false);
  const [thinking, setThinking] = React.useState(false);
  const [messageEntered, setMessageEntered] = React.useState(false);
  const [ctaEntered, setCtaEntered] = React.useState(false);
  const [typedMessage, setTypedMessage] = React.useState("");
  const [typedDetail, setTypedDetail] = React.useState("");
  const [typingComplete, setTypingComplete] = React.useState(false);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const forcedGuidancePreview = isForcedGuidancePreview();
  const externallyMature = matureOverride === true && !forcedGuidancePreview;

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const refresh = (event) => {
      const completedStep = event?.detail?.step;
      if (completedStep) markDoctorReadinessStepComplete(completedStep);
      setRefreshTick((tick) => tick + 1);
    };
    window.addEventListener("afrimed:profile:refresh", refresh);
    window.addEventListener("afrimed:fola:refresh", refresh);
    window.addEventListener(DOCTOR_READINESS_STEP_COMPLETE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("afrimed:profile:refresh", refresh);
      window.removeEventListener("afrimed:fola:refresh", refresh);
      window.removeEventListener(DOCTOR_READINESS_STEP_COMPLETE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  React.useEffect(() => {
    let alive = true;

    async function loadState() {
      if (!userId) {
        setState((current) => ({ ...current, loading: false }));
        return;
      }

      if (shouldResetDismissals()) clearStoredDismissals(userId, effectiveRole);
      const localDismissed = shouldResetDismissals() ? {} : getStoredDismissals(userId, effectiveRole, profile);
      const persistedDismissed = shouldResetDismissals()
        ? {}
        : await loadPersistedDismissals(userId, effectiveRole);
      const dismissed = { ...persistedDismissed, ...localDismissed };

      try {
        if (effectiveRole === "doctor") {
          const [
            doctorResult,
            consultanciesResult,
            servicesResult,
            pricesResult,
            appointmentResult,
            prescriptionsResult,
            payoutResult,
          ] = await Promise.all([
            supabase
              .from("doctor_profiles")
              .select("*")
              .eq("user_id", userId)
              .maybeSingle(),
            supabase
              .from("doctor_consultancies")
              .select("id,facility_name,patients_age,availability_text,insurance_policy,payment_terms")
              .eq("doctor_id", userId)
              .limit(20),
            supabase
              .from("doctor_services")
              .select("id", { count: "exact", head: true })
              .eq("doctor_id", userId)
              .eq("is_active", true),
            supabase
              .from("doctor_service_prices")
              .select("id", { count: "exact", head: true })
              .eq("doctor_user_id", userId)
              .eq("is_active", true),
            supabase
              .from("appointments")
              .select("id", { count: "exact", head: true })
              .eq("doctor_id", userId),
            supabase
              .from("prescriptions")
              .select("id", { count: "exact", head: true })
              .eq("doctor_user_id", userId),
            supabase.functions.invoke("clinician-payout", { body: { action: "status" } }).catch(() => ({ data: null })),
          ]);

          if (!alive) return;
          const doctorIds = Array.from(
            new Set(
              [userId, doctorResult?.data?.id]
                .map((id) => String(id || "").trim())
                .filter(Boolean)
            )
          );
          let consultancies = Array.isArray(consultanciesResult?.data)
            ? consultanciesResult.data
            : [];
          let servicesCount = Number(servicesResult?.count || 0);
          let appointmentCount = Number(appointmentResult?.count || 0);
          let prescriptionsCount = Number(prescriptionsResult?.count || 0);
          if (doctorIds.length > 1) {
            const [
              linkedConsultanciesResult,
              linkedServicesResult,
              linkedAppointmentResult,
              linkedPrescriptionsResult,
            ] = await Promise.all([
              supabase
                .from("doctor_consultancies")
                .select("id,facility_name,patients_age,availability_text,insurance_policy,payment_terms")
                .in("doctor_id", doctorIds)
                .limit(20),
              supabase
                .from("doctor_services")
                .select("id", { count: "exact", head: true })
                .in("doctor_id", doctorIds)
                .eq("is_active", true),
              appointmentCount
                ? Promise.resolve({ count: appointmentCount })
                : supabase
                    .from("appointments")
                    .select("id", { count: "exact", head: true })
                    .in("doctor_id", doctorIds),
              prescriptionsCount
                ? Promise.resolve({ count: prescriptionsCount })
                : supabase
                    .from("prescriptions")
                    .select("id", { count: "exact", head: true })
                    .in("doctor_user_id", doctorIds),
            ]);
            consultancies = Array.isArray(linkedConsultanciesResult?.data)
              ? linkedConsultanciesResult.data
              : consultancies;
            servicesCount = Number(linkedServicesResult?.count || servicesCount || 0);
            appointmentCount = Number(linkedAppointmentResult?.count || appointmentCount || 0);
            prescriptionsCount = Number(linkedPrescriptionsResult?.count || prescriptionsCount || 0);
          }
          if (!alive) return;
          setState({
            loading: false,
            patientProfile: null,
            patientEhrInfo: null,
            doctorProfile: doctorResult?.data ? { ...doctorResult.data, canonical_payout_verified: payoutResult?.data?.profile?.verification_status === "verified" } : null,
            consultancies,
            servicesCount,
            pricesCount: Number(pricesResult?.count || 0),
            prescriptionsCount,
            appointmentCount,
            galleryCount: Array.isArray(doctorResult?.data?.gallery_photo_paths)
              ? doctorResult.data.gallery_photo_paths.filter((path) => String(path || "").trim()).length
              : 0,
            eVisitEnabled: doctorResult?.data?.evisit_enabled === true,
            dismissed,
          });
          return;
        }

        const [patientResult, patientIdsResult] = await Promise.all([
          supabase
            .from("patient_profiles")
            .select("*")
            .eq("user_id", userId)
            .maybeSingle(),
          supabase
            .from("patients")
            .select("id")
            .eq("user_id", userId),
        ]);

        const appointmentPatientIds = Array.from(
          new Set(
            [
              userId,
              patientResult?.data?.id,
              ...(Array.isArray(patientIdsResult?.data)
                ? patientIdsResult.data.map((row) => row?.id)
                : []),
            ]
              .map((value) => String(value || "").trim())
              .filter(Boolean),
          ),
        );
        const appointmentResult = appointmentPatientIds.length
          ? await supabase
              .from("appointments")
              .select("id", { count: "exact", head: true })
              .in("patient_id", appointmentPatientIds)
              .eq("status", "completed")
          : { count: 0 };
        const patientEhrResult = await supabase
          .from("ehr_patient_info")
          .select("patient_id,primary_language,height_cm,weight_kg,blood_type,current_medications,allergies,chronic_conditions")
          .eq("patient_id", userId)
          .maybeSingle();

        if (!alive) return;
        setState({
          loading: false,
          patientProfile: patientResult?.data || null,
          patientEhrInfo: patientEhrResult?.data || null,
          doctorProfile: null,
          consultancies: [],
          servicesCount: 0,
          pricesCount: 0,
          prescriptionsCount: 0,
          appointmentCount: Number(appointmentResult?.count || 0),
          galleryCount: 0,
          eVisitEnabled: false,
          dismissed,
        });
      } catch {
        if (!alive) return;
        setState((current) => ({ ...current, loading: false, dismissed }));
      }
    }

    loadState();
    return () => {
      alive = false;
    };
  }, [effectiveRole, placement, profile, refreshTick, userId]);

  const rawStep = React.useMemo(() => {
    if (!userId) return null;
    if (externallyMature) return null;
    if (state.loading) return null;
    if (effectiveRole === "doctor") {
      const readinessStep = resolveDoctorReadinessNextStep({
        userId,
        profile,
        user,
        doctorProfile: state.doctorProfile,
        consultancies: state.consultancies,
        servicesCount: state.servicesCount,
        pricesCount: state.pricesCount,
        eVisitEnabled: state.eVisitEnabled,
        currentPath: location.pathname,
        t,
      });
      if (DOCTOR_CONTEXTUAL_PLACEMENTS.has(placement)) {
        return buildDoctorPlacementStep({
          placement,
          role: effectiveRole,
          profile,
          user,
          doctorProfile: state.doctorProfile,
          readinessStep,
          t,
        });
      }
      return readinessStep;
    }
    const mature = isMatureForRole({
      role: effectiveRole,
      patientProfile: state.patientProfile,
      doctorProfile: state.doctorProfile,
      profile,
      user,
      consultancies: state.consultancies,
      servicesCount: state.servicesCount,
      pricesCount: state.pricesCount,
      galleryCount: state.galleryCount,
      appointmentCount: state.appointmentCount,
      prescriptionsCount: state.prescriptionsCount,
      eVisitEnabled: state.eVisitEnabled,
    });
    if (FINAL_VISIBILITY_RULES_ENABLED && mature && !forcedGuidancePreview) return null;

    if (placement !== "settings") {
      if (
        ["patientHome", "patientAppointments"].includes(placement) &&
        !hasFirstUseOrientationPending(profile, "patient")
      ) {
        return null;
      }
      return buildPlacementStep({
        placement,
        role: effectiveRole,
        profile,
        user,
        patientProfile: state.patientProfile,
        t,
      });
    }

    return resolvePatientReadinessNextStep({
      patientProfile: state.patientProfile,
      patientEhrInfo: state.patientEhrInfo,
      profile,
      currentPath: location.pathname,
      dismissed: state.dismissed,
      userId,
      t,
    });
  }, [effectiveRole, externallyMature, forcedGuidancePreview, location.pathname, placement, profile, state, t, user, userId]);

  // Route is context only. A card is eligible solely for an unfinished,
  // persisted requirement with a specific action that is not duplicated by
  // the current edit surface.
  const step = React.useMemo(
    () => (isTruthGatedSmartCardStep(rawStep, location.pathname) ? rawStep : null),
    [location.pathname, rawStep],
  );

  const matureForDebug = React.useMemo(
    () =>
      isMatureForRole({
        role: effectiveRole,
        patientProfile: state.patientProfile,
        patientEhrInfo: state.patientEhrInfo,
        doctorProfile: state.doctorProfile,
        profile,
        user,
        consultancies: state.consultancies,
        servicesCount: state.servicesCount,
        pricesCount: state.pricesCount,
        galleryCount: state.galleryCount,
        appointmentCount: state.appointmentCount,
        prescriptionsCount: state.prescriptionsCount,
        eVisitEnabled: state.eVisitEnabled,
      }),
    [effectiveRole, profile, state, user],
  );

  React.useEffect(() => {
    if (!isFolaDebugEnabled() || state.loading) return;
    const cardKey = step?.key || FOLA_FLOW_STEPS[placement]?.key || (effectiveRole === "doctor" ? DOCTOR_CONSULTANCY_STEP_KEY : PATIENT_PROFILE_STEP_KEY);
    const dismissed = Boolean(cardKey && state.dismissed[cardKey]);
    const reasonHidden = step
      ? dismissed
        ? "dismissed"
        : ""
      : externallyMature
        ? "external_mature_override"
        : matureForDebug && !forcedGuidancePreview
          ? "mature_operational"
          : FOLA_FLOW_STEPS[placement]?.role && FOLA_FLOW_STEPS[placement].role !== effectiveRole
            ? "role_mismatch"
            : "no_eligible_step";
    console.warn("[fola-card-gate]", {
      route: location.pathname,
      userId,
      role: effectiveRole,
      cardKey,
      visible: Boolean(step && !dismissed),
      reasonHidden,
      maturitySignals:
        effectiveRole === "doctor"
          ? buildDoctorMaturitySignals({
              appointmentCount: state.appointmentCount,
              prescriptionsCount: state.prescriptionsCount,
              doctorProfile: state.doctorProfile,
              profile,
              consultancies: state.consultancies,
              servicesCount: state.servicesCount,
              pricesCount: state.pricesCount,
              eVisitEnabled: state.eVisitEnabled,
            })
          : { appointments_count: Number(state.appointmentCount || 0) },
      dismissed,
    });
  }, [
    effectiveRole,
    externallyMature,
    forcedGuidancePreview,
    location.pathname,
    matureForDebug,
    placement,
    state,
    step,
    userId,
  ]);

  React.useEffect(() => {
    if (!step) {
      setEntered(false);
      setThinking(false);
      setMessageEntered(false);
      setCtaEntered(false);
      setTypedMessage("");
      setTypedDetail("");
      setTypingComplete(false);
      return undefined;
    }

    setEntered(false);
    setThinking(false);
    setMessageEntered(false);
    setCtaEntered(false);
    setTypedMessage("");
    setTypedDetail("");
    setTypingComplete(false);

    const entryTimer = window.setTimeout(() => setEntered(true), 560);
    const thinkingTimer = window.setTimeout(() => setThinking(true), 640);
    const messageTimer = window.setTimeout(() => {
      setThinking(false);
      setMessageEntered(true);
    }, 1760);

    return () => {
      window.clearTimeout(entryTimer);
      window.clearTimeout(thinkingTimer);
      window.clearTimeout(messageTimer);
    };
  }, [step?.key]);

  React.useEffect(() => {
    if (!step || !messageEntered) return undefined;

    const message = String(step.message || "");
    const detail = String(step.detail || "");
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setTypedMessage(message);
      setTypedDetail(detail);
      setTypingComplete(true);
      setCtaEntered(true);
      return undefined;
    }

    setTypedMessage("");
    setTypedDetail("");
    setTypingComplete(false);
    setCtaEntered(false);

    const fullText = detail ? `${message}\n${detail}` : message;
    let index = 0;
    const charsPerTick = fullText.length > 140 ? 2 : 1;
    const interval = window.setInterval(() => {
      index = Math.min(fullText.length, index + charsPerTick);
      const nextText = fullText.slice(0, index);
      const [nextMessage = "", ...detailParts] = nextText.split("\n");
      setTypedMessage(nextMessage);
      setTypedDetail(detailParts.join("\n"));

      if (index >= fullText.length) {
        window.clearInterval(interval);
        setTypingComplete(true);
        window.setTimeout(() => setCtaEntered(true), 160);
      }
    }, 34);

    return () => window.clearInterval(interval);
  }, [messageEntered, step?.detail, step?.key, step?.message]);

  React.useEffect(() => {
    if (!step || effectiveRole !== "patient" || placement !== "patientProfile") return;
    markPatientProfileReviewSeen(userId);
  }, [effectiveRole, placement, step?.key, userId]);

  if (!step) return null;
  const patientGloballyDismissed = Boolean(
    effectiveRole === "patient" && state.dismissed[PATIENT_GLOBAL_DISMISSAL_KEY],
  );
  const isDismissed = Boolean(state.dismissed[step.key]);
  if (patientGloballyDismissed) return null;
  if (isDismissed) return null;

  const dismiss = async () => {
    const dismissedAt = new Date().toISOString();
    let ownerId = userId;
    if (!ownerId) {
      try {
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();
        ownerId = String(authUser?.id || "").trim();
      } catch {
        ownerId = "";
      }
    }
    const dismissalKeys =
      effectiveRole === "patient"
        ? [step.key, PATIENT_GLOBAL_DISMISSAL_KEY]
        : [step.key];
    dismissalKeys.forEach((key) => setStoredDismissal(ownerId || userId, effectiveRole, key));
    await Promise.all(
      dismissalKeys.map((key) => persistDismissal(ownerId || userId, effectiveRole, key, dismissedAt)),
    );
    setState((current) => ({
      ...current,
      dismissed: {
        ...current.dismissed,
        ...Object.fromEntries(dismissalKeys.map((key) => [key, dismissedAt])),
      },
    }));
  };
  const isCompact = Boolean(step.compact);

  return (
    <Card
      as="section"
      className={[
        "relative scroll-mt-24 scroll-mb-28 overflow-visible bg-[rgba(var(--afrimed-primary),0.045)] text-on-surface",
        isCompact ? "px-3 py-2.5" : "px-4 py-3",
        "ring-1 ring-[rgba(var(--afrimed-primary),0.035)]",
        "transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none",
        entered ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
        className,
      ].join(" ")}
      aria-label={t("fola.onboarding.title", "Fola")}
    >
      <style>
        {`
          @keyframes afrimed-fola-dot {
            0%, 80%, 100% { opacity: .34; transform: translateY(0); }
            40% { opacity: 1; transform: translateY(-2px); }
          }
        `}
      </style>
      <div>
        <div className="min-w-0">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className={FOLA_COMPACT_HEADER_ROW_CLASS}>
                <img
                  src={FOLA_ICON}
                  alt=""
                  className={[
                    "transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none",
                    FOLA_ICON_CLASS,
                    entered ? "scale-100 opacity-100" : "scale-95 opacity-0",
                  ].join(" ")}
                  draggable="false"
                />
                <p className={FOLA_LABEL_CLASS}>
                  {t("fola.onboarding.label", "Fola")}
                </p>
              </div>
              <div className={isCompact ? "mt-1 min-h-[52px]" : "mt-1 min-h-[58px]"}>
                <div
                  className={[
                    "flex h-6 items-center gap-1 transition-opacity duration-150 motion-reduce:transition-none",
                    thinking && !messageEntered ? "opacity-100" : "pointer-events-none opacity-0",
                  ].join(" ")}
                  aria-hidden={!thinking || messageEntered}
                >
                  {[0, 1, 2].map((dot) => (
                    <span
                      key={dot}
                      className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--afrimed-primary))]"
                      style={{
                        animation: "afrimed-fola-dot 780ms ease-in-out infinite",
                        animationDelay: `${dot * 120}ms`,
                      }}
                    />
                  ))}
                </div>
                <div
                  className={[
                    "transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none",
                    messageEntered ? "-mt-6 translate-y-0 opacity-100" : "-mt-6 translate-y-1 opacity-0",
                  ].join(" ")}
                >
                  <p className="text-label-large text-on-surface">
                    {typedMessage}
                    {messageEntered && !typingComplete ? (
                      <span className="ml-0.5 inline-block h-[1em] w-[2px] translate-y-0.5 bg-[rgb(var(--afrimed-primary))]" aria-hidden="true" />
                    ) : null}
                  </p>
                  {step.detail && (typedDetail || typingComplete) ? (
                    <p className="mt-1 text-body-small text-on-surface-var">
                      {typedDetail}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            {step.dismissible ? (
              <button
                type="button"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full opacity-85 transition-opacity hover:opacity-100"
                data-testid="fola-card-dismiss"
                aria-label={t("common.dismiss", "Dismiss")}
                onClick={dismiss}
              >
                <img src={CLOSE_ICON} alt="" className="h-5 w-5 opacity-80" draggable="false" />
              </button>
            ) : null}
          </div>

          {step.cta ? (
            <button
              type="button"
              className={[
                "inline-flex min-h-10 items-center rounded-xl bg-[rgb(var(--afrimed-primary))] px-4 text-label-large text-[rgb(var(--afrimed-on-primary))]",
                "transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none",
                ctaEntered ? "mt-1.5 translate-y-0 opacity-100" : "mt-1.5 translate-y-1 opacity-0",
              ].join(" ")}
              onClick={() => {
                if (onCtaClick) {
                  onCtaClick();
                  return;
                }
                if (step.path) {
                  const shouldNavigate = window.dispatchEvent(
                    new CustomEvent(DOCTOR_READINESS_NEXT_STEP_EVENT, {
                      cancelable: true,
                      detail: {
                        path: step.path,
                        stepKey: step.key,
                        missingItem: step.missing_item || step.missingItem || "",
                      },
                    }),
                  );
                  if (shouldNavigate) navigate(step.path);
                }
              }}
            >
              {step.cta}
            </button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
